import { asError } from '#/lib/asError';
import { queryClient } from '#/lib/init';
import { isReservationEditable } from '#/lib/isReservationEditable';
import { reservationDataQueryKeys } from '#/services/queryKeys';
import RentalsReservation, { RentalsReservationInstance } from '@jucy-nakuru/common/services/JucyRentalsApi/RentalsReservation';
import { autorun, makeAutoObservable, reaction, runInAction } from 'mobx';
import ErrorReporter from '../lib/ErrorReporter';
import AuthStore from './AuthStore';
import RentalBranchStore from './RentalBranchStore';
import TranslationStore from './TranslationStore';

const getPersistedRef = () => (typeof window !== 'undefined' ? window.localStorage.getItem('activeRef') || undefined : undefined);
const setPersistedRef = (selectedReservationRef: string | undefined) => {
    if (typeof window === 'undefined') {
        return;
    }
    const current = getPersistedRef();

    if (selectedReservationRef && selectedReservationRef !== current) {
        localStorage.setItem('activeRef', selectedReservationRef);
    } else if (!selectedReservationRef) {
        localStorage.removeItem('activeRef');
    }
};
export default class AppStateStore {
    profileModalOpen = false;
    leftSidebarOpen = true;
    liveChatOpen = false;
    liveChatLoaded = false;
    languageNZ = 'nz';
    authStore?: AuthStore;
    translationStore?: TranslationStore;
    rentalBranchStore?: RentalBranchStore;
    selectedReservationRef: string = '';
    isLoading: boolean = false;
    loadingRef: string = '';
    selectedReservation?: RentalsReservationInstance = undefined;
    isSelectedReservationEditable: boolean = false;
    activeRegion: 'au' | 'nz' = 'nz';
    constructor(initialData?: Partial<AppStateStore>) {
        makeAutoObservable(this);
        Object.assign(this, initialData);

        reaction(
            () => this.selectedReservationRef,
            async (selectedReservationRef) => {
                setPersistedRef(selectedReservationRef);
            }
        );

        reaction(
            () => this.selectedReservation,
            async (selectedReservation) => {
                if (!selectedReservation) {
                    this.isSelectedReservationEditable = false;
                    return;
                }

                this.isSelectedReservationEditable = isReservationEditable(this.selectedReservation);
                await this.updateTranslationStore(selectedReservation);
            }
        );

        autorun(async () => {
            const selectedReservationRef = this.selectedReservationRef;
            runInAction(() => {
                this.loadingRef = selectedReservationRef;
                this.isLoading = true;
            });
            try {
                await this.refreshSelectedReservation();
            } catch (error) {
                ErrorReporter.reportError({ error: asError(error) });
                runInAction(() => {
                    this.selectedReservationRef = '';
                });
            } finally {
                runInAction(() => {
                    this.loadingRef = '';
                    this.isLoading = false;
                });
            }
        });

        const persistedActiveRef = getPersistedRef();
        if (persistedActiveRef) {
            runInAction(() => {
                this.selectedReservationRef = persistedActiveRef;
            });
        }

        reaction(
            () => this.selectedReservation,
            async (selectedReservation) => {
                const branch = selectedReservation?.pickUpLocationCode
                    ? await this.rentalBranchStore?.getBranchByCode(selectedReservation?.pickUpLocationCode)
                    : null;
                if (branch?.countryCode) {
                    this.activeRegion = branch?.countryCode as 'au' | 'nz';
                }
            }
        );
    }

    hydrate = (data: Partial<AppStateStore>): void => {
        Object.assign(this, data);
    };

    private updateTranslationStore = async (reservation: RentalsReservation) => {
        if (!this.translationStore || !reservation?.pickUpLocationCode) {
            return;
        }

        const site = await this.rentalBranchStore?.getBranchByCode(reservation.pickUpLocationCode);
        this.translationStore?.setNameSpaceForSite(site);
    };

    async refreshSelectedReservation({ force }: { force?: boolean } = { force: false }) {
        const selfServiceApiClient = this.authStore?.selfServiceApiClient;
        const selectedReservationRef = this.selectedReservationRef;
        const currentReservationRef = this.selectedReservation?.reservationReference;

        if (!selectedReservationRef) {
            return;
        }
        if (!force && selectedReservationRef === currentReservationRef) {
            return;
        }
        if (!selfServiceApiClient) {
            return;
        }

        const reservation = await queryClient.fetchQuery({
            queryKey: reservationDataQueryKeys.data(selectedReservationRef),
            queryFn: async () => selfServiceApiClient.getReservation(selectedReservationRef),
        });
        runInAction(() => {
            this.selectedReservation = reservation;
        });
    }
}
